import { inject } from '@angular/core';
import {
  type ActivatedRouteSnapshot,
  type CanActivateFn,
  Router,
  type UrlTree,
} from '@angular/router';

import { CapabilitiesService } from '../capabilities/capabilities.service';
import { AUTH_CONFIG } from '../session/auth.config';

/**
 * Guards for the `/partner/:id/...` surfaces, where the partner is named
 * explicitly in the URL rather than picked from an ambient switcher (there is
 * none yet). Kept separate from the coarse access guards in `auth.guards.ts`:
 * these are about *which* partner is active, not *whether* the user is allowed.
 *
 * Both answer from the session's `partnerAccess` rather than from the URL or
 * the stored context alone — one to resolve a partner that is knowable but not
 * yet chosen, the other to refuse one that was never the user's to name.
 */

/**
 * Where these guards send someone they will not let through — the same
 * `capabilityDeniedUrl ?? forbiddenUrl` rule `requireCapabilities` uses, so an
 * app configures one landing and gets it from every fine-grained refusal. In
 * `partner-area` that is `/no-access`, inside the shell, where the membership
 * gate and the partner switcher render.
 *
 * Must be called from inside a guard's injection context.
 */
function deniedUrl(): UrlTree {
  const { forbiddenUrl, capabilityDeniedUrl } = inject(AUTH_CONFIG);
  return inject(Router).createUrlTree([capabilityDeniedUrl ?? forbiddenUrl]);
}

/**
 * Point the auth layer at the partner named in the URL, provided it is one of
 * the user's own. `/partner/:id/...` routes are gated on that partner's
 * capabilities, and the API interceptor sends the active partner as the
 * `creatorSub` header, so the active partner must be set from `:id` *before*
 * the capability guard runs. Order it ahead of `requireCapabilities(...)` in a
 * route's `canActivate`.
 *
 * An `:id` the user does not staff is **refused here**, rather than written and
 * left for the capability guard to reject (HAL-150). Writing it first poisoned
 * the context: the choice persists to `localStorage`, so the collapsed nav and
 * the nameless switcher survived the denial and every page load after it. But
 * merely declining to write it would lose the denial altogether — the previous
 * partner would stay active, its capabilities would still pass the gate, and
 * one partner's data would render under another partner's URL. The check and
 * the refusal have to sit in the same place.
 *
 * A super admin passes: their `partnerAccess` is empty by design and they act
 * on every area, mirroring the server-side bypass.
 */
export const setActivePartnerFromRoute: CanActivateFn = (
  route: ActivatedRouteSnapshot,
): boolean | UrlTree => {
  const id = route.paramMap.get('id');
  if (!id) {
    return true;
  }
  const caps = inject(CapabilitiesService);
  if (!caps.canActOnPartner(id)) {
    return deniedUrl();
  }
  caps.setActivePartnerId(id);
  return true;
};

/**
 * Canonicalise a static sidebar link (e.g. `/members`) to the active partner's
 * id URL (`/partner/:id/members`), which is where the `requireCapabilities(...)`
 * gate lives — the alias itself carries none, so rendering it here would show
 * the feature ungated and with no active partner.
 *
 * The partner comes from {@link CapabilitiesService.resolveActivePartnerId},
 * not from `activePartnerId()` directly, and that is the whole of HAL-149. The
 * sole-partner auto-select is a constructor `effect`, which has not flushed
 * when the router runs guards on a cold load, so reading the signal made a
 * single-partner user look like a user with no partner at all — and every deep
 * link, including every link followed through login, was quietly redirected to
 * `/dashboard` with no denial and no trace of where it had been going.
 *
 * `subPath` is the segment(s) after the partner id — `redirectToActivePartner('members')`
 * → `/partner/:id/members`, `redirectToActivePartner('roles')` → `/partner/:id/roles`.
 *
 * A segment written as `':name'` is substituted from the aliased route's own
 * params, so an alias that carries an identifier keeps it:
 * `redirectToActivePartner('compliance', 'approvals', ':requestId')` on
 * `compliance/approvals/:requestId` → `/partner/:id/compliance/approvals/areq-1`.
 * Without that, an alias for a parameterised route would drop the very thing the
 * URL was about and land the reader on a detail page for nothing. A `:name` the
 * route does not carry falls through as the literal segment rather than
 * disappearing, so the mistake is visible in the URL bar instead of silent.
 *
 * The query string and fragment are carried across, because this is the same page
 * under its canonical URL — dropping them would silently discard state the target
 * route reads. `/settings/sso?tab=roles` is the live case: `withComponentInputBinding`
 * binds `tab` to an input, so losing it lands every shared or bookmarked deep link on
 * the default panel with no sign anything was lost.
 *
 * When the partner genuinely cannot be resolved — several areas with none
 * picked, or none at all — the navigation is refused to {@link deniedUrl}
 * rather than pointed at the dashboard. The user is not entitled to a page
 * under no partner in particular, and a redirect that looks like an ordinary
 * landing is how the original defect stayed invisible. The fallback keeps
 * neither the query nor the fragment: that is a different page, and the
 * parameters would not mean anything there.
 */
export function redirectToActivePartner(...subPath: string[]): CanActivateFn {
  return (route: ActivatedRouteSnapshot): UrlTree => {
    const caps = inject(CapabilitiesService);
    const router = inject(Router);
    const id = caps.resolveActivePartnerId();
    if (!id) return deniedUrl();

    const segments = subPath.map((segment) =>
      segment.startsWith(':') ? (route.paramMap.get(segment.slice(1)) ?? segment) : segment,
    );
    return router.createUrlTree(['partner', id, ...segments], {
      queryParams: route.queryParams,
      fragment: route.fragment ?? undefined,
    });
  };
}
